import buildStationObject from './build_station_object';
import promiseTimeout from './promise_timeout';

// When NATE runner state is not loaded before this timeout it is considered as not running.
const DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT = 10000;

// When NATE state is not loaded before this timeout it is considered as not running.
const DEFAULT_NATE_REQUEST_TIMEOUT = 10000;

class StationError extends Error {
  constructor({ host, status, cause = null }) {
    super();

    if (Error.captureStackTrace) Error.captureStackTrace(this, StationError);

    this.name = 'StationError';
    this.host = host;
    this.status = status;
    this.cause = cause;
  }

  toStationObject() {
    return buildStationObject({ host: this.host, status: this.status });
  }
}

async function refreshStation({
  host,
  nateRunnerRequestTimeout = DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT,
  nateRequestTimeout = DEFAULT_NATE_REQUEST_TIMEOUT,
}) {
  const nateRunnerApiUrl = `http://${host}`;

  let nateRunnerStateResponse;
  try {
    nateRunnerStateResponse = await Promise.race([
      fetch(`${nateRunnerApiUrl}/state`),
      promiseTimeout(nateRunnerRequestTimeout, () => new StationError({ host, status: 'nate_runner_not_running' })),
    ]);
  } catch (error) {
    if (error.name === 'StationError') throw error;

    throw new StationError({ host, cause: error, status: 'nate_runner_state_fetch_error' });
  }

  let nateRunnerState;
  try {
    nateRunnerState = await nateRunnerStateResponse.json();
  } catch (error) {
    throw new StationError({ host, cause: error, status: 'nate_runner_state_json_error' });
  }

  if (!nateRunnerState?.data?.nate?.api_url) {
    console.warn(host, nateRunnerState);

    throw new StationError({ host, status: 'nate_runner_state_missing_api_url' });
  }

  const nateApiUrl = nateRunnerState?.data?.nate?.api_url;

  let nateStateResponse;
  try {
    nateStateResponse = await Promise.race([
      fetch(`${nateApiUrl}/state`),
      promiseTimeout(nateRequestTimeout, () => new StationError({ host, status: 'nate_not_running' })),
    ]);
  } catch (error) {
    if (error.name === 'StationError') throw error;

    throw new StationError({ host, cause: error, status: 'nate_state_fetch_error' });
  }

  let nateState;
  try {
    nateState = await nateStateResponse.json();
  } catch (error) {
    throw new StationError({ host, cause: error, status: 'nate_state_json_error' });
  }

  return buildStationObject({ host, status: 'ok', nateRunnerApiUrl, nateRunnerState, nateApiUrl, nateState });
}

export default async function safeRefreshStation({
  host,
  nateRunnerRequestTimeout = DEFAULT_NATE_RUNNER_REQUEST_TIMEOUT,
  nateRequestTimeout = DEFAULT_NATE_REQUEST_TIMEOUT,
}) {
  return refreshStation({ host, nateRunnerRequestTimeout, nateRequestTimeout }).catch((error) => {
    if (error.name !== 'StationError') console.warn(error);

    const stationError =
      error.name === 'StationError' ? error : new StationError({ host, cause: error, status: 'unknown_error' });

    return stationError.toStationObject();
  });
}
